class WhatYouLearn extends HTMLElement {
  #scrollRaf = 0;
  #metricsRaf = 0;
  #snapRestoreTimer = 0;

  connectedCallback() {
    this.viewport = this.querySelector('[data-wyl-viewport]');
    this.track = this.querySelector('[data-wyl-track]');
    this.slides = this.track ? [...this.track.querySelectorAll('[data-wyl-slide], .wyl-card')] : [];
    this.dots = [...this.querySelectorAll('[data-wyl-dot]')];
    this.nav = this.querySelector('[data-wyl-nav]');
    this.index = 0;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const target = Number(dot.dataset.wylDot);
        if (!Number.isNaN(target)) this.goTo(target);
      });
    });

    this.viewport?.addEventListener('scroll', this.#onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.#scheduleMetrics());
    if (this.viewport) this.resizeObserver.observe(this.viewport);
    if (this.track) this.resizeObserver.observe(this.track);

    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.mediaQuery.addEventListener('change', this.#onBreakpoint);

    this.#scheduleMetrics();
    this.updateControls();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.viewport?.removeEventListener('scroll', this.#onScroll);
    this.mediaQuery?.removeEventListener('change', this.#onBreakpoint);
    cancelAnimationFrame(this.#scrollRaf);
    cancelAnimationFrame(this.#metricsRaf);
    window.clearTimeout(this.#snapRestoreTimer);
  }

  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  getVisibleCount() {
    const styles = getComputedStyle(this);
    const visible = Number.parseFloat(styles.getPropertyValue('--wyl-visible-mobile')) || 1.5;
    return visible > 0 ? visible : 1.5;
  }

  getMaxIndex() {
    return Math.max(0, this.slides.length - 1);
  }

  getStep() {
    if (!this.slides.length || !this.track) return 0;
    const gap =
      Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    return this.slides[0].offsetWidth + gap;
  }

  getMaxScroll() {
    if (!this.viewport) return 0;
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  /**
   * @param {number} index
   * Last page scrolls to max so the last card sits flush-right (previous peeks left).
   */
  getScrollLeftForIndex(index) {
    const maxIndex = this.getMaxIndex();
    if (index >= maxIndex) return this.getMaxScroll();
    return Math.max(0, index * this.getStep());
  }

  /** @param {number} target */
  goTo(target) {
    this.index = Math.min(this.getMaxIndex(), Math.max(0, target));

    if (!this.isMobile() || !this.viewport) {
      this.updateControls();
      return;
    }

    const maxIndex = this.getMaxIndex();
    const left = this.getScrollLeftForIndex(this.index);

    if (this.index >= maxIndex) {
      // Avoid snap-align:start settling with blank on the right
      this.viewport.style.scrollSnapType = 'none';
      this.viewport.scrollTo({ left, behavior: 'smooth' });
      window.clearTimeout(this.#snapRestoreTimer);
      this.#snapRestoreTimer = window.setTimeout(() => {
        if (!this.viewport) return;
        this.viewport.scrollLeft = this.getMaxScroll();
        this.viewport.style.scrollSnapType = '';
      }, 320);
    } else {
      this.viewport.style.scrollSnapType = '';
      this.viewport.scrollTo({ left, behavior: 'smooth' });
    }

    this.updateControls();
  }

  #onBreakpoint = () => {
    this.#scheduleMetrics();
    if (!this.isMobile() && this.viewport) {
      this.viewport.style.scrollSnapType = '';
      this.viewport.scrollLeft = 0;
      this.index = 0;
    }
    this.updateControls();
  };

  #scheduleMetrics = () => {
    cancelAnimationFrame(this.#metricsRaf);
    this.#metricsRaf = requestAnimationFrame(() => {
      this.#setSlideMetrics();
      this.updateControls();
    });
  };

  #setSlideMetrics() {
    if (!this.viewport || !this.isMobile()) {
      this.style.removeProperty('--wyl-slide-width');
      this.style.removeProperty('--wyl-end-padding');
      return;
    }

    const styles = getComputedStyle(this);
    const gap = Number.parseFloat(styles.getPropertyValue('--wyl-mobile-gap')) || 10;
    const visible = this.getVisibleCount();
    const available = Math.max(0, this.viewport.clientWidth);
    const slideWidth = Math.max(0, (available - gap * Math.max(0, visible - 1)) / visible);

    this.style.setProperty('--wyl-slide-width', `${slideWidth}px`);
    // Only a small end inset — do NOT pad a half-card blank on the right
    this.style.setProperty('--wyl-end-padding', '16px');
  }

  #resolveIndexFromScroll() {
    const maxIndex = this.getMaxIndex();
    const step = this.getStep();
    if (!step || !this.viewport) return 0;
    if (maxIndex <= 0) return 0;

    const scrollLeft = this.viewport.scrollLeft;
    const maxScroll = this.getMaxScroll();
    const prevLeft = (maxIndex - 1) * step;
    const lastMid = (prevLeft + maxScroll) / 2;

    if (maxScroll > 0 && scrollLeft >= lastMid) return maxIndex;
    return Math.min(maxIndex - 1, Math.max(0, Math.round(scrollLeft / step)));
  }

  #onScroll = () => {
    if (!this.isMobile() || !this.viewport || !this.slides.length) return;

    cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      this.index = this.#resolveIndexFromScroll();
      this.updateControls();
    });
  };

  updateControls() {
    this.dots.forEach((dot, i) => {
      const isActive = i === this.index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('what-you-learn')) {
  customElements.define('what-you-learn', WhatYouLearn);
}
